import { useState } from "react";

const BANKS = [
  { value: "auto",  label: "Auto Detect" },
  { value: "sbi",   label: "State Bank of India" },
  { value: "hdfc",  label: "HDFC Bank" },
  { value: "icici", label: "ICICI Bank" },
  { value: "axis",  label: "Axis Bank" },
  { value: "kotak", label: "Kotak Mahindra Bank" },
];

export default function BankStatement() {
  const [file, setFile] = useState<File | null>(null);
  const [clientName, setClientName] = useState("");
  const [bankHint, setBankHint] = useState("auto");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [reconciled, setReconciled] = useState<boolean | null>(null);

  const handleUpload = async () => {
    if (!file) { setError("Please select a file"); return; }
    setLoading(true); setError(""); setSuccess(false); setReconciled(null);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("client_name", clientName);
    formData.append("bank_hint", bankHint);
    try {
      const response = await fetch("/api/bank-statement/convert", {
        method: "POST",
        body: formData,
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || "Conversion failed");
      }
      const contentDisposition = response.headers.get("content-disposition") || "";
      const isReconciled = response.headers.get("x-reconciled") !== "false";
      setReconciled(isReconciled);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "TaxThinker_BankStatement_" + (clientName || "Report") + ".xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: "100%", padding: "10px 14px",
    border: "1.5px solid var(--border)",
    borderRadius: "8px", fontSize: "14px",
    color: "var(--text-primary)",
    background: "var(--white)", outline: "none"
  };

  return (
    <div style={{ maxWidth: "720px", margin: "0 auto", padding: "40px 24px" }}>
      <div style={{ textAlign: "center", marginBottom: "32px" }}>
        <span style={{ fontSize: "52px" }}>🏦</span>
        <h1 style={{ fontSize: "28px", fontWeight: 800, color: "var(--text-primary)", margin: "12px 0 8px" }}>
          Bank Statement to Excel
        </h1>
        <p style={{ color: "var(--text-muted)", fontSize: "14px", maxWidth: "480px", margin: "0 auto", lineHeight: "1.7" }}>
          Upload your bank statement PDF or Excel file. Get a clean, formatted Excel with
          smart narration and a receipts/payments summary.
        </p>
      </div>

      <div style={{ background: "var(--card-bg)", borderRadius: "16px", border: "1.5px solid var(--border)", padding: "32px" }}>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "20px" }}>
          <div>
            <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: "var(--text-secondary)", marginBottom: "6px" }}>
              Client Name (optional)
            </label>
            <input
              type="text"
              value={clientName}
              onChange={e => setClientName(e.target.value)}
              placeholder="e.g. Ramesh Kumar"
              style={inputStyle}
            />
          </div>
          <div>
            <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: "var(--text-secondary)", marginBottom: "6px" }}>
              Bank (or Auto Detect)
            </label>
            <select
              value={bankHint}
              onChange={e => setBankHint(e.target.value)}
              style={inputStyle}>
              {BANKS.map(b => (
                <option key={b.value} value={b.value}>{b.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div
          style={{ border: "2px dashed var(--border)", borderRadius: "12px", padding: "36px", textAlign: "center", cursor: "pointer", transition: "border-color 0.2s", marginBottom: "20px" }}
          onClick={() => document.getElementById("bsFileInput")?.click()}
          onMouseEnter={e => { e.currentTarget.style.borderColor = "#C9A84C"; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--border)"; }}>
          <input
            id="bsFileInput"
            type="file"
            accept=".pdf,.xlsx,.xls,.csv"
            style={{ display: "none" }}
            onChange={e => setFile(e.target.files?.[0] || null)}
          />
          {file ? (
            <div>
              <div style={{ fontSize: "36px", marginBottom: "8px" }}>
                {file.name.endsWith(".pdf") ? "📄" : "📊"}
              </div>
              <p style={{ fontWeight: 600, color: "var(--text-primary)", marginBottom: "4px" }}>{file.name}</p>
              <p style={{ fontSize: "12px", color: "var(--text-muted)" }}>{(file.size / 1024).toFixed(1)} KB</p>
              <button
                onClick={e => { e.stopPropagation(); setFile(null); }}
                style={{ marginTop: "8px", fontSize: "12px", color: "#C9A84C", background: "transparent", border: "none", cursor: "pointer" }}>
                Change file
              </button>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: "36px", marginBottom: "10px" }}>☁️</div>
              <p style={{ fontWeight: 600, color: "var(--text-primary)", marginBottom: "4px" }}>
                Click to upload bank statement
              </p>
              <p style={{ fontSize: "12px", color: "var(--text-muted)" }}>
                PDF, Excel (.xlsx/.xls) or CSV
              </p>
            </div>
          )}
        </div>

        {error && (
          <div style={{ background: "var(--offwhite)", border: "1px solid #FCE4D6", borderRadius: "8px", padding: "12px 16px", marginBottom: "16px", fontSize: "13px", color: "#9C0006" }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{ background: "#E2EFDA", border: "1px solid #A9C9A0", borderRadius: "8px", padding: "14px 16px", marginBottom: "16px" }}>
            <div style={{ fontSize: "14px", fontWeight: 700, color: "#375623", marginBottom: "4px" }}>
              Bank statement converted successfully!
            </div>
            {reconciled === true && (
              <div style={{ fontSize: "13px", color: "#375623" }}>
                Reconciliation passed — all transactions verified.
              </div>
            )}
            {reconciled === false && (
              <div style={{ fontSize: "13px", color: "#7F3F00" }}>
                Reconciliation warning — please check the Excel for details.
              </div>
            )}
          </div>
        )}

        <button
          onClick={handleUpload}
          disabled={!file || loading}
          style={{ width: "100%", padding: "13px", background: file && !loading ? "#C9A84C" : "var(--border)", color: file && !loading ? "#2C2C2C" : "var(--text-muted)", border: "none", borderRadius: "10px", fontSize: "15px", fontWeight: 700, cursor: file && !loading ? "pointer" : "not-allowed", transition: "background 0.2s" }}>
          {loading ? "Converting... Please wait (30-60 seconds)" : "Convert to Excel"}
        </button>

        <div style={{ marginTop: "20px", background: "var(--offwhite)", borderRadius: "10px", padding: "16px" }}>
          <p style={{ fontSize: "13px", fontWeight: 700, color: "var(--text-primary)", marginBottom: "10px" }}>
            Your Excel will contain:
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
            {[
              "All transactions in table format",
              "Smart Narration column (AI-powered)",
              "Receipts summary with SUMIFS",
              "Payments summary with SUMIFS",
              "Reconciliation verification",
              "Currency formatting throughout",
            ].map((item, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: "var(--text-secondary)" }}>
                <span style={{ color: "#C9A84C", fontWeight: 700 }}>✓</span> {item}
              </div>
            ))}
          </div>
        </div>

        <div style={{ marginTop: "16px", padding: "12px 16px", background: "#FFF2CC", borderRadius: "8px", fontSize: "12px", color: "#7F6000" }}>
          Supports: SBI, HDFC Bank, ICICI Bank, Axis Bank, Kotak Bank and most other Indian banks.
        </div>
      </div>
    </div>
  );
}
